import { ApiLoadingState, MFA_CHALLENGE_FALLBACK_MESSAGES, useAuth, UserStatus } from "@canucci/base/auth";
import { Box, Center } from "@mantine/core";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { MFA_CHALLENGE_I18N_KEYS } from "./loginPageI18n";
import { useLoginActions } from "./useLoginActions";
import { useLoginForm } from "./useLoginForm";
import { useLoginTranslations } from "./useLoginTranslations";
import styles from "./LoginPage.module.css";
import { LoginDeactivatedView } from "./views/LoginDeactivatedView";
import { LoginEmailVerificationView } from "./views/LoginEmailVerificationView";
import { LoginForgotPasswordView } from "./views/LoginForgotPasswordView";
import { LoginFormView } from "./views/LoginFormView";
import { LoginLoadingView } from "./views/LoginLoadingView";
import { LoginMfaChallengeView } from "./views/LoginMfaChallengeView";
import { LoginPendingApprovalView } from "./views/LoginPendingApprovalView";

export type LoginPageTranslate = (key: string, vars?: Record<string, string>) => string;

/** Optional link for "Terms and Privacy" / "Contact support" footer. */
export interface LoginPageFooterLink {
  label: string;
  href: string;
}

export interface LoginPageProps {
  onLoginSuccess: () => void;
  /** Heading above the form. Omit/`undefined` to use the localized default; pass `null` to hide it. */
  title?: ReactNode;
  /** Subtitle below the title. Omit/`undefined` to use the localized default; pass `null` to hide it. */
  subtitle?: ReactNode;
  logo?: ReactNode;
  translate?: LoginPageTranslate;
  /** When true, show sign-up form instead of sign-in. Enables deep-linking to signup (e.g. /signup). */
  initialSignUp?: boolean;
  /** Optional email hint used to prefill the email field for invite/signup links. */
  initialEmail?: string;
  /** Called when user toggles between sign-in and sign-up so the host can sync URL (e.g. navigate to /signup or /login). */
  onSignUpModeChange?: (isSignUp: boolean) => void;
  initialErrorCode?: "user_not_found" | null;
  /** Error message from URL (e.g. after redirect from Firebase handler). Shown once then URL is cleared. */
  initialErrorMessage?: string | null;
  /** When true, show a one-time green banner (host should strip ?verified=true after mount). */
  emailVerifiedFromLink?: boolean;
  /** Banner body; default copy invites the user to sign in. */
  emailVerifiedFromLinkMessage?: string;
  /** Optional footer line, e.g. "By signing in, you agree to Terms and Privacy". Rendered as small text below the form. */
  footerAgreement?: ReactNode;
  /** Optional support link shown below the form. */
  footerSupportLink?: LoginPageFooterLink;
  /** Optional host-specific notice for the post-signup verify-email screen. */
  getVerifyEmailNotice?: (email: string, supportHref?: string) => ReactNode | undefined;
}

export function LoginPage({
  onLoginSuccess,
  title,
  subtitle,
  logo,
  translate,
  initialSignUp,
  initialEmail,
  onSignUpModeChange,
  initialErrorCode,
  initialErrorMessage,
  emailVerifiedFromLink,
  emailVerifiedFromLinkMessage = "Your email is verified. Sign in with your email and password.",
  footerAgreement,
  footerSupportLink,
  getVerifyEmailNotice,
}: LoginPageProps) {
  const {
    loading,
    login,
    signUp,
    loginWithGoogle,
    loginWithMicrosoft,
    sendPasswordResetEmail: sendPasswordResetEmailToUser,
    isProcessingDeepLink,
    user,
    isEmailVerified,
    permissions,
    logout,
    resendEmailVerification,
    syncEmailVerificationFromFirebase,
    deepLink,
    authError,
    authErrorKind,
    clearAuthError,
    mfaChallengePending,
    completeMfaChallenge,
    cancelMfaChallenge,
  } = useAuth();
  const onLoginSuccessRef = useRef(onLoginSuccess);
  // `isProcessingDeepLink` is also true during an ordinary sign-in. Only an
  // observed parked link means AuthContext owns the post-login navigation.
  const deepLinkNavigationOwnedRef = useRef(deepLink !== null);
  const hasTriggeredLoginSuccessRef = useRef(false);
  if (deepLink !== null) {
    deepLinkNavigationOwnedRef.current = true;
  }
  useEffect(() => {
    onLoginSuccessRef.current = onLoginSuccess;
  });

  const tr = useLoginTranslations(translate);
  const { form, isSignUp, setIsSignUpWithErrorClear, error, setError } = useLoginForm(tr, initialSignUp, initialEmail);
  const [showEmailVerifiedBanner, setShowEmailVerifiedBanner] = useState(Boolean(emailVerifiedFromLink));
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const previousEmailVerifiedFromLink = useRef(Boolean(emailVerifiedFromLink));

  // Show redirect-flow error (e.g. user returned from Google with auth/unauthorized-domain) — then clear it
  useEffect(() => {
    if (authErrorKind) {
      setError(tr(MFA_CHALLENGE_I18N_KEYS[authErrorKind], MFA_CHALLENGE_FALLBACK_MESSAGES[authErrorKind]));
      clearAuthError();
    } else if (authError) {
      setError(authError);
      clearAuthError();
    }
  }, [authError, authErrorKind, setError, clearAuthError, tr]);
  const actions = useLoginActions(
    {
      login,
      signUp,
      loginWithGoogle,
      loginWithMicrosoft,
      sendPasswordResetEmail: sendPasswordResetEmailToUser,
      resendEmailVerification,
    },
    () => form.values,
    tr,
    setError,
  );

  useEffect(() => {
    if (initialErrorCode === "user_not_found") {
      setError(tr("login.errors.userNotFoundContactAdmin", "User not found. Please contact your administrator."));
    }
  }, [initialErrorCode, setError, tr]);

  // Show error from URL (e.g. Firebase handler redirected back with ?error=auth/unauthorized-domain)
  useEffect(() => {
    if (initialErrorMessage?.trim()) {
      setError(initialErrorMessage.trim());
    }
  }, [initialErrorMessage, setError]);

  // Consume `emailVerifiedFromLink` on rising edge only. Host pages may keep the prop true in state.
  useEffect(() => {
    const current = Boolean(emailVerifiedFromLink);
    if (current && !previousEmailVerifiedFromLink.current) {
      setShowEmailVerifiedBanner(true);
    }
    previousEmailVerifiedFromLink.current = current;
  }, [emailVerifiedFromLink]);

  const hasDeepLink = deepLink !== null;

  // Hide the one-time verified banner once the user leaves the plain login form flow.
  useEffect(() => {
    if (!showEmailVerifiedBanner) return;
    if (user || isProcessingDeepLink || permissions?.status === UserStatus.PENDING) {
      setShowEmailVerifiedBanner(false);
    }
  }, [showEmailVerifiedBanner, user, isProcessingDeepLink, permissions?.status]);

  useEffect(() => {
    if (!user || isEmailVerified || permissions?.status !== UserStatus.PENDING) return;
    const sync = () => {
      void syncEmailVerificationFromFirebase().catch((syncError: unknown) => {
        console.warn("Failed to sync email verification status.", syncError);
      });
    };
    sync();
    const onVisibility = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [user, isEmailVerified, permissions?.status, syncEmailVerificationFromFirebase]);

  useEffect(() => {
    if (!user || isProcessingDeepLink || hasDeepLink || permissions?.status !== UserStatus.ACTIVE) {
      hasTriggeredLoginSuccessRef.current = false;
      return;
    }

    // AuthContext owns navigation once a parked deep link has been observed. When it
    // clears that link after arrival, do not overwrite the restored URL with the
    // host application's default post-login destination.
    if (!deepLinkNavigationOwnedRef.current && !hasTriggeredLoginSuccessRef.current) {
      hasTriggeredLoginSuccessRef.current = true;
      onLoginSuccessRef.current();
    }
  }, [user, isProcessingDeepLink, hasDeepLink, permissions]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    form.validate();
    if (Object.keys(form.errors).length > 0) return;
    if (isSignUp) {
      void actions.handleEmailPasswordSignUp();
    } else {
      void actions.handleEmailPasswordSignIn();
    }
  };

  const handleForgotPasswordSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = form.values.email?.trim();
    if (!email || !/^\S+@\S+$/.test(email)) {
      form.setFieldError("email", tr("login.validation.invalidEmail", "Invalid email"));
      return;
    }
    void actions.handleForgotPassword();
  };

  const handleBackToSignIn = useCallback(() => {
    form.reset();
    setIsSignUpWithErrorClear(false);
    setShowForgotPassword(false);
    onSignUpModeChange?.(false);
    setShowEmailVerifiedBanner(false);
    void logout();
  }, [form, setIsSignUpWithErrorClear, onSignUpModeChange, logout]);

  const handleToggleSignUp = useCallback(() => {
    setShowForgotPassword(false);
    const next = !isSignUp;
    setIsSignUpWithErrorClear(next);
    onSignUpModeChange?.(next);
  }, [isSignUp, setIsSignUpWithErrorClear, onSignUpModeChange]);

  const handleShowForgotPassword = useCallback(() => {
    setError("");
    actions.clearResetPasswordMessage();
    setShowForgotPassword(true);
  }, [setError, actions]);

  const handleHideForgotPassword = useCallback(() => {
    setError("");
    actions.clearResetPasswordMessage();
    setShowForgotPassword(false);
  }, [setError, actions]);

  const handleCancelMfaChallenge = useCallback(() => {
    setError("");
    cancelMfaChallenge();
  }, [cancelMfaChallenge, setError]);

  let content: ReactNode;
  if (mfaChallengePending) {
    content = (
      <LoginMfaChallengeView onSubmit={completeMfaChallenge} onCancel={handleCancelMfaChallenge} logo={logo} tr={tr} />
    );
  } else if (loading === ApiLoadingState.loading || isProcessingDeepLink) {
    content = <LoginLoadingView isProcessingDeepLink={isProcessingDeepLink} tr={tr} />;
  } else if (user && !isEmailVerified && permissions?.status === UserStatus.PENDING) {
    content = (
      <LoginEmailVerificationView
        userEmail={user.email ?? ""}
        resendMessage={actions.resendMessage}
        resendLoading={actions.resendLoading}
        onResend={actions.handleResendVerification}
        onChangeAccount={handleBackToSignIn}
        logo={logo}
        footerAgreement={footerAgreement}
        footerSupportLink={footerSupportLink}
        instructionsOverride={getVerifyEmailNotice?.(user.email ?? "", footerSupportLink?.href)}
        tr={tr}
      />
    );
  } else if (permissions?.status === UserStatus.PENDING) {
    content = (
      <LoginPendingApprovalView
        onChangeAccount={handleBackToSignIn}
        logo={logo}
        footerAgreement={footerAgreement}
        footerSupportLink={footerSupportLink}
        tr={tr}
      />
    );
  } else if (permissions?.status === UserStatus.INACTIVE) {
    content = <LoginDeactivatedView onChangeAccount={handleBackToSignIn} logo={logo} tr={tr} />;
  } else if (showForgotPassword) {
    content = (
      <LoginForgotPasswordView
        emailInputProps={form.getInputProps("email")}
        onBack={handleHideForgotPassword}
        onSubmit={handleForgotPasswordSubmit}
        resetPasswordLoading={actions.resetPasswordLoading}
        resetPasswordMessage={actions.resetPasswordMessage}
        error={error}
        logo={logo}
        tr={tr}
      />
    );
  } else {
    content = (
      <LoginFormView
        form={form}
        isSignUp={isSignUp}
        onToggleSignUp={handleToggleSignUp}
        onForgotPasswordClick={handleShowForgotPassword}
        error={error}
        onSubmit={handleSubmit}
        onGoogleSignIn={actions.handleGoogleSignIn}
        onMicrosoftSignIn={actions.handleMicrosoftSignIn}
        authLoading={actions.authLoading}
        socialLoading={actions.socialLoading}
        title={title}
        subtitle={subtitle}
        logo={logo}
        footerAgreement={footerAgreement}
        footerSupportLink={footerSupportLink}
        emailVerifiedFromLink={showEmailVerifiedBanner}
        emailVerifiedFromLinkMessage={emailVerifiedFromLinkMessage}
        tr={tr}
      />
    );
  }

  return (
    <Box className={styles.root}>
      <Center className={styles.shell}>{content}</Center>
    </Box>
  );
}
